import ScoreEvidenceView from './scoring/ScoreEvidenceView';
import React from 'react';
import { MessageDetail, PublicationView, StructuredArticle } from './types';
import {
  autoDecisionLabels,
  commandStatusLabels,
  commandTypeLabels,
  formatBeijingTime,
  importanceLabels
} from './reviewUiState';

const matchType = { STOCK_CODE: '股票代码', STOCK_NAME: '股票名称', THEME_NAME: '题材名称' } as const;

const asItems = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

function ItemList({ label, items }: { label: string; items: string[] }) {
  return (
    <>
      <h4>{label}</h4>
      {items.length ? (
        <ul>
          {items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      ) : (
        <p>该版本未提供</p>
      )}
    </>
  );
}

/** Read-only projection of the structured article fields. */
export function StructuredArticleView({
  article,
  missing = '该版本未提供'
}: {
  article: Partial<StructuredArticle> | null | undefined;
  missing?: string;
}) {
  if (!article) {
    return <p>{missing}</p>;
  }
  return (
    <>
      <p>
        主题：
        {article.topic || missing}
      </p>
      <ItemList label="关键事实" items={asItems(article.keyFacts)} />
      <h4>短期影响</h4>
      <pre>{article.shortTermImpact || missing}</pre>
      <h4>中期影响</h4>
      <pre>{article.mediumTermImpact || missing}</pre>
      <h4>行动指引</h4>
      <pre>{article.actionGuidance || missing}</pre>
      <h4>事件还原</h4>
      <pre>{article.eventReconstruction || missing}</pre>
      <h4>可信度说明</h4>
      <pre>{article.confidenceAnalysis || missing}</pre>
      <ItemList label="传导路径" items={asItems(article.transmissionPath)} />
      <h4>预期差</h4>
      <pre>{article.expectationGap || missing}</pre>
      <ItemList label="行动清单" items={asItems(article.actionItems)} />
    </>
  );
}

function SourceEvidence({ detail }: { detail: MessageDetail }) {
  return (
    <div className="message-evidence">
      <ScoreEvidenceView contentId={detail.id} />
      <section aria-label="原文">
        <h3>标准化原文</h3>
        {detail.text ? (
          <>
            <p>
              原文状态：{detail.text.status === 'READY' ? '已就绪' : detail.text.status} · 来源时间：
              {formatBeijingTime(detail.text.sourcePublishedAt, '来源未提供')}
            </p>
            <h4>{detail.text.title}</h4>
            <pre>{detail.text.content}</pre>
            {detail.text.sourceUrl && (
              <p>
                来源链接：
                <a href={detail.text.sourceUrl} target="_blank" rel="noopener noreferrer">
                  {detail.text.sourceUrl}
                </a>
              </p>
            )}
            {detail.text.reviewReason && <p>{detail.text.reviewReason}</p>}
          </>
        ) : (
          <p>暂无标准化原文</p>
        )}
      </section>
      <section aria-label="AI 分析">
        <h3>AI 分析</h3>
        {detail.analysis ? (
          <>
            <p>
              分析状态：{detail.analysis.status === 'FAILED' ? '失败' : '已生成'} · 有效性：
              {detail.analysis.validity === 'VALID' ? '有效' : detail.analysis.validity || '未提供'} · 重要程度：
              {detail.analysis.importance ? importanceLabels[detail.analysis.importance] : '未提供'}
            </p>
            <p>{detail.analysis.reason}</p>
            <h4>摘要</h4>
            <pre>{detail.analysis.summary}</pre>
            <h4>解读</h4>
            <pre>{detail.analysis.interpretation}</pre>
            <h4>结构化文章</h4>
            <StructuredArticleView article={detail.analysis} />
            <h4>证据</h4>
            <pre>{JSON.stringify(detail.analysis.evidence, null, 2)}</pre>
            <h4>缺失事实</h4>
            <pre>{JSON.stringify(detail.analysis.missingFacts, null, 2)}</pre>
          </>
        ) : (
          <p>暂无 AI 分析</p>
        )}
      </section>
      <section aria-label="处理状态">
        <h3>当前来源版本处理状态</h3>
        {detail.processing.length ? (
          detail.processing.map(job => (
            <p key={job.id}>
              {job.stage} · {job.status} · 尝试 {job.attempts} 次 · {job.outcome || '尚无处理结果'} ·{' '}
              {job.errorCode || '无错误码'} · 下次可处理：{formatBeijingTime(job.availableAt)} · 完成：
              {formatBeijingTime(job.finishedAt, '未完成')}
            </p>
          ))
        ) : (
          <p>暂无处理作业</p>
        )}
      </section>
    </div>
  );
}

function PublicationSnapshot({ detail }: { detail: MessageDetail }) {
  const publication = detail.publication;
  if (!publication) {
    return <p>当前没有发布记录。</p>;
  }
  return (
    <div className="message-publication">
      <p>
        {publication.visible ? '用户当前看到的内容' : '最近发布记录，当前不可见'} · 发布来源：
        {publication.origin === 'AUTOMATIC' ? '自动发布' : '人工发布'}
      </p>
      <p>
        公开时间：{formatBeijingTime(publication.publishedAt)} · 来源时间：
        {formatBeijingTime(publication.sourcePublishedAt)}
      </p>
      <p>
        重要程度：{importanceLabels[publication.importance] ?? publication.importance} ·{' '}
        {publication.featured ? '精选' : '未精选'}
        {publication.readingMinutes != null ? ` · 阅读 ${publication.readingMinutes} 分钟` : ''}
      </p>
      {publication.sourceUrl && (
        <p>
          来源链接：
          <a href={publication.sourceUrl} target="_blank" rel="noopener noreferrer">
            {publication.sourceUrl}
          </a>
        </p>
      )}
      <h3>{publication.title}</h3>
      <pre>{publication.summary}</pre>
      <pre>{publication.interpretation}</pre>
      <StructuredArticleView article={publication} />
      <details>
        <summary>技术详情</summary>
        <p>
          处理记录状态：{publication.status} · 发布版本：{publication.publicationVersionId} · 发布时间：
          {publication.publishedAt}
        </p>
        <p>
          来源版本 {publication.sourceVersion} · 审核版本 {publication.editorialVersion} · 审核编号{' '}
          {publication.reviewId || '无'}
        </p>
      </details>
    </div>
  );
}

function OperationLog({ detail }: { detail: MessageDetail }) {
  return (
    <div className="message-history">
      <section aria-label="自动发布决策">
        <h3>自动发布决策</h3>
        {detail.autoDecisions.length ? (
          detail.autoDecisions.map(decision => (
            <details key={decision.id} open={decision.id === detail.publication?.autoDecisionId}>
              <summary>
                策略 {decision.policyCode} v{decision.policyVersion} · {autoDecisionLabels[decision.decision] ?? decision.decision}{' '}
                · {decision.status === 'PUBLISHED' ? '已公开' : decision.status}
              </summary>
              <p>
                原因：{decision.reasonCode} · 最终级别：
                {decision.finalImportance ? importanceLabels[decision.finalImportance] : '无'} ·{' '}
                {decision.featured ? '自动精选' : '未精选'} · 行情日：{decision.marketTradeDate || '无可用行情'}
              </p>
              <p>
                来源版本 {decision.sourceVersion} · 审核版本 {decision.editorialVersion} · 发布版本{' '}
                {decision.publicationVersionId || '未生成'}
              </p>
              <h4>行情/题材匹配</h4>
              {decision.marketMatches.length ? (
                <ul>
                  {decision.marketMatches.map((match, index) => (
                    <li key={`${match.type}-${match.stockId || match.themeNodeId}-${index}`}>
                      {matchType[match.type]}“{match.matchedText}” · {match.hot ? '命中热点' : '普通匹配'}
                      {match.changePct === null ? '' : ` · 涨跌幅 ${match.changePct}%`}
                      {match.tradeDate ? ` · ${match.tradeDate}` : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>未匹配到股票或题材</p>
              )}
              <details>
                <summary>决策上下文</summary>
                <pre>{JSON.stringify(decision.context, null, 2)}</pre>
              </details>
            </details>
          ))
        ) : (
          <p>暂无自动发布决策</p>
        )}
      </section>
      <section aria-label="审核历史">
        <h3>审核历史</h3>
        {detail.reviews.length ? (
          detail.reviews.map(review => (
            <details key={review.id}>
              <summary>
                审核版本 {review.editorialVersion} · {review.actor} · {formatBeijingTime(review.createdAt)}
              </summary>
              <p>
                来源版本 {review.sourceVersion} · 分析 {review.analysisId} · {importanceLabels[review.importance]} ·{' '}
                {review.featured ? '精选' : '普通'}
              </p>
              <h4>{review.title}</h4>
              <pre>{review.summary}</pre>
              <pre>{review.interpretation}</pre>
              <StructuredArticleView article={review} />
              <p>原因：{review.reason}</p>
            </details>
          ))
        ) : (
          <p>暂无审核历史</p>
        )}
      </section>
      <section aria-label="命令历史">
        <h3>发布与拦截记录</h3>
        {detail.commands.length ? (
          detail.commands.map(command => (
            <details key={command.commandId}>
              <summary>
                {commandTypeLabels[command.type] ?? command.type} · {commandStatusLabels[command.status] ?? command.status}
                {command.resultCode ? ` · ${command.resultCode}` : ''}
              </summary>
              <p>
                {command.commandId} · {command.actor} · {formatBeijingTime(command.createdAt)}
              </p>
              <p>原因：{command.reason}</p>
              <p>完成：{formatBeijingTime(command.finishedAt, '未完成')}</p>
            </details>
          ))
        ) : (
          <p>暂无命令历史</p>
        )}
      </section>
    </div>
  );
}

export default function MessageEvidence({
  detail,
  pane = 'all'
}: {
  detail: MessageDetail;
  pane?: 'source' | 'publication' | 'history' | 'all';
}) {
  if (pane === 'source') return <SourceEvidence detail={detail} />;
  if (pane === 'publication') return <PublicationSnapshot detail={detail} />;
  if (pane === 'history') return <OperationLog detail={detail} />;
  return (
    <div className="message-evidence-all">
      <SourceEvidence detail={detail} />
      <PublicationSnapshot detail={detail} />
      <OperationLog detail={detail} />
    </div>
  );
}

export type { PublicationView };
